import { fabric } from 'fabric';
import path from 'path';
import { getCachedData, setCacheData } from '../utils/cache';

import {
  getActiveCanvas,
  getStrokeLineCap,
  modifyUrl,
  getOriginalUrl,
  inToPx,
  isEmpty,
  mapObject,
  pxToIn,
  scaleSize,
  ptToPx,
  pxToPt
} from '../utils';
import { DEFAULT_RULE_DATA, applyShadowToObject } from './common';
import {
  DEFAULT_IMAGE,
  OBJECT_TYPE,
  IMAGE_LOCAL,
  IMAGE_INDICATOR,
  VIDEO_EVENT_TYPE,
  VIDEO_PLAY_ICON,
  CROP_CONTROL,
  DEFAULT_VIDEO,
  PORTRAIT_IMAGE_MASK,
  LOWSIZE_IMAGE
} from '../constants';
import {
  renderImageCropControl,
  videoInitEvent,
  useObjectControlsOverride,
  useDoubleStroke,
  renderMappingIcon,
  imageBorderModifier
} from '@/plugins/fabric';
import { updateSpecificProp } from '@/common/fabricObjects';
import axios from 'axios';

/**
 * Create new fabric image width initial properties
 * @param {Object} props initial properties of Image box
 * @returns {Promise<Object>} instance of Image
 */
export const createImage = props => {
  return new Promise(resolve => {
    const fabricProp = toFabricMediaProp(props);
    const {
      size: { width, height },
      type
    } = props;
    const { left, top, id, imageUrl } = fabricProp;

    const src =
      type === OBJECT_TYPE.IMAGE && imageUrl
        ? imageUrl
        : DEFAULT_IMAGE.IMAGE_URL;

    fabric.util.loadImage(
      modifyUrl(src),
      img => {
        const image = new fabric.Image(img, {
          ...fabricProp,
          id,
          lockUniScaling: false
        });

        const adjustedWidth = inToPx(width) || image.width;
        const adjustedHeight = inToPx(height) || image.height;
        const scaleX = adjustedWidth / image.width;
        const scaleY = adjustedHeight / image.height;

        image.set({ left, top, scaleX, scaleY });

        resolve({
          object: image,
          size: {
            width: pxToIn(adjustedWidth),
            height: pxToIn(adjustedHeight)
          }
        });
      },
      null,
      {
        crossOrigin: 'anonymous'
      }
    );
  });
};
export const calculateRxRy = fabricProp => {
  const { width, mask, height } = fabricProp;
  let { cornerRounding } = fabricProp;
  if (isEmpty(cornerRounding)) {
    cornerRounding = 0;
  }
  let rx = 0;
  let ry = 0;
  const radiusRatio = mask === PORTRAIT_IMAGE_MASK.ROUNDED ? 10 : 2;
  const adjustedCornerRounding = ptToPx(cornerRounding);

  if (mask === PORTRAIT_IMAGE_MASK.CIRCLE) {
    rx = width / radiusRatio;
    ry = height / radiusRatio;
  } else {
    const d = Math.min(width, height);
    rx = ry =
      adjustedCornerRounding === 0 ? 0 : d * (adjustedCornerRounding / d);
  }
  return {
    rx,
    ry
  };
};

/**
 * Create new fabric image width initial properties
 * @param {Object} props initial properties of Image box
 * @returns {Promise} instance of Image
 */
export const createNewImage = (props, isPreviewPDF) => {
  const promise = new Promise((resolve, reject) =>
    (async () => {
      const fabricProp = toFabricNewMediaProp(props);
      const { width, id, mask, objectType } = fabricProp;
      let {
        cornerRounding,
        imageUrl,
        lowsizeUrl,
        hasImage: boxHasImage
      } = fabricProp;
      const hasImage = !!imageUrl && boxHasImage;

      if (objectType === OBJECT_TYPE.IMAGE && !imageUrl) {
        imageUrl = DEFAULT_IMAGE.IMAGE_URL;
        lowsizeUrl = null;
      }

      if (!isPreviewPDF && hasImage) {
        imageUrl = await generateImageBlob(lowsizeUrl, imageUrl);
      }

      if (isEmpty(cornerRounding)) {
        cornerRounding = 0;
      }
      const { rx, ry } = calculateRxRy(fabricProp);
      let rect = {};
      if (fabricProp?.mask === PORTRAIT_IMAGE_MASK.CIRCLE) {
        rect = new fabric.Ellipse({
          ...fabricProp,
          id,
          width,
          mask,
          rx,
          ry,
          fill: 'transparent',
          strokeUniform: true,
          imageUrl
        });
      } else {
        rect = new fabric.Rect({
          ...fabricProp,
          id,
          width,
          mask,
          rx,
          ry,
          fill: 'transparent',
          strokeUniform: true,
          imageUrl,
          cornerRounding
        });
      }

      const adjustedWidth = inToPx(props.width) || rect.width;
      const adjustedHeight = inToPx(props.height) || rect.height;

      createMediaOverlay(imageUrl)
        .then(img => {
          rect.set({ img });
          const resolveData = {
            object: rect,
            size: {
              width: pxToIn(adjustedWidth),
              height: pxToIn(adjustedHeight)
            }
          };
          resolve(resolveData);
        })
        .catch(error => {
          reject(error);
        });
    })()
  );
  return promise;
};

const extractScaleValue = decodedUrl => {
  if (decodedUrl.indexOf(LOWSIZE_IMAGE.URL_REDUCE_SIZE_PARAM) === -1) {
    return 1;
  }

  const regexPattern = /\/(\d+(\.\d+)?)(?=\/)/;
  const match = decodedUrl.match(regexPattern);
  return match && parseFloat(match[1]) > 0 ? 100 / parseFloat(match[1]) : 1;
};

const extractExtension = url => {
  return path.extname(new URL(url).pathname).toLowerCase() === '.png'
    ? 'png'
    : 'jpeg';
};

const getImageProps = lowsizeUrl => {
  let scaleValue = 1;
  let extension = 'jpeg';
  const splitUrl = lowsizeUrl.split(LOWSIZE_IMAGE.URL_PARAM_SEPARATOR);
  if (splitUrl.length === 2) {
    const decodedUrl = Buffer.from(splitUrl[1], 'base64').toString('ascii');
    scaleValue = extractScaleValue(decodedUrl);

    const srcUrl = decodedUrl.split(LOWSIZE_IMAGE.URL_SRC_PARAM);
    if (srcUrl.length === 2) {
      extension = extractExtension(decodeURIComponent(srcUrl[1]));
    }
  } else {
    extension = extractExtension(lowsizeUrl);
  }

  return {
    scale: scaleValue,
    extension: extension
  };
};
const handleImageProcessing = async (url, props, cacheName, cacheKey) => {
  const blob = await processImage(url, props.scale, props.extension);
  if (blob.size == 0) {
    throw new Error('Process failed');
  }
  await setCacheData(cacheName, cacheKey, blob);
  return URL.createObjectURL(blob);
};

const generateImageBlob = (lowsizeUrl, originalUrl) => {
  const url = lowsizeUrl || originalUrl;

  if (!url.startsWith('http')) {
    return originalUrl;
  }

  return new Promise(resolve =>
    (async () => {
      try {
        const props = getImageProps(url);
        const cacheName = 'blob-fullsize-scaled';
        const cacheKey = `url=${url}-scale=${props.scale}-extension=${props.extension}`;
        const cachedData = await getCachedData(cacheName, cacheKey);
        if (cachedData) {
          const blob = await cachedData.blob();
          if (blob.size == 0) {
            throw new Error('Get cache failed');
          }

          const blobUrl = URL.createObjectURL(blob);
          resolve(blobUrl);
        } else {
          const newUrl = await handleImageProcessing(
            url,
            props,
            cacheName,
            cacheKey
          );
          resolve(newUrl);
        }
      } catch (error) {
        // ensure that fallback url should have cors param  to prevent cors issue with browser cache
        const fallbackUrl = modifyUrl(originalUrl);
        console.log(error, 'Fallback to url=', fallbackUrl);
        resolve(fallbackUrl);
      }
    })()
  );
};

const processImage = (url, scale, extension) => {
  return new Promise((resolve, reject) => {
    if (scale == 1) {
      const httpsUrl = url.replace('http://', 'https://');
      axios
        .get(modifyUrl(httpsUrl), {
          responseType: 'blob'
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    } else {
      fabric.Image.fromURL(
        modifyUrl(url),
        function(img) {
          const canvas = new fabric.StaticCanvas();
          canvas.setDimensions({
            width: img.width * scale,
            height: img.height * scale
          });
          img.scale(scale);
          canvas.add(img);
          canvas.renderAll();

          const base64Image = canvas.toDataURL({
            format: extension,
            quality: 1
          });

          const buffer = Buffer.from(base64Image.split(',')[1], 'base64');
          const blob = new Blob([buffer], { type: 'image/' + extension });

          resolve(blob);
        },
        { crossOrigin: 'anonymous' }
      );
    }
  });
};

/**
 * Convert stored image properties to fabric properties
 *
 * @param   {Object}  prop  stored image properties
 * @returns {Object}        fabric properties
 */
export const toFabricMediaProp = (prop, originalElement) => {
  const mapRules = {
    data: {
      type: DEFAULT_RULE_DATA.TYPE,
      x: DEFAULT_RULE_DATA.X,
      y: DEFAULT_RULE_DATA.Y,
      horizontal: DEFAULT_RULE_DATA.HORIZONTAL,
      vertical: DEFAULT_RULE_DATA.VERTICAL,
      rotation: DEFAULT_RULE_DATA.ROTATION,
      width: {
        name: 'scaleX',
        parse: value => {
          if (originalElement) {
            return inToPx(value) / originalElement.width;
          }
          return 1;
        }
      },
      height: {
        name: 'scaleY',
        parse: value => {
          if (originalElement) {
            return inToPx(value) / originalElement.height;
          }
          return 1;
        }
      }
    },
    restrict: ['border', 'cropInfo']
  };
  return mapObject(prop, mapRules);
};

/**
 * Convert stored image properties to fabric properties
 *
 * @param   {Object}  prop  stored image properties
 * @returns {Object}        fabric properties
 */
export const toFabricPortraitImageProp = prop => {
  const mapRules = {
    data: {
      type: DEFAULT_RULE_DATA.TYPE,
      x: DEFAULT_RULE_DATA.X,
      y: DEFAULT_RULE_DATA.Y,
      width: DEFAULT_RULE_DATA.WIDTH,
      height: DEFAULT_RULE_DATA.HEIGHT,
      rx: {
        name: 'rx',
        parse: inToPx
      },
      ry: {
        name: 'ry',
        parse: inToPx
      },
      horizontal: DEFAULT_RULE_DATA.HORIZONTAL,
      vertical: DEFAULT_RULE_DATA.VERTICAL,
      rotation: DEFAULT_RULE_DATA.ROTATION
    },
    restrict: ['border', 'shadow', 'cropInfo']
  };
  return mapObject(prop, mapRules);
};

/**
 * Convert stored image properties to fabric properties
 *
 * @param   {Object}  prop  stored image properties
 * @returns {Object}        fabric properties
 */
export const toFabricNewMediaProp = prop => {
  const mapRules = {
    data: {
      type: DEFAULT_RULE_DATA.TYPE,
      x: DEFAULT_RULE_DATA.X,
      y: DEFAULT_RULE_DATA.Y,
      width: DEFAULT_RULE_DATA.WIDTH,
      height: DEFAULT_RULE_DATA.HEIGHT,
      rx: {
        name: 'rx',
        parse: inToPx
      },
      ry: {
        name: 'ry',
        parse: inToPx
      },
      horizontal: DEFAULT_RULE_DATA.HORIZONTAL,
      vertical: DEFAULT_RULE_DATA.VERTICAL,
      rotation: DEFAULT_RULE_DATA.ROTATION
    },
    restrict: ['border', 'cropInfo']
  };
  return mapObject(prop, mapRules);
};

/**
 * Convert stored image border properties to fabric properties
 *
 * @param   {Object}  style stored image border properties
 * @returns {Object}        fabric properties
 */
export const toFabricImageBorderProp = prop => {
  const mapRules = {
    data: {
      strokeWidth: {
        name: 'strokeWidth',
        parse: value => scaleSize(value)
      }
    },
    restrict: [
      'id',
      'shadow',
      'size',
      'flip',
      'rotation',
      'isConstrain',
      'style'
    ]
  };

  return mapObject(prop, mapRules);
};

/**
 * Apply layout on an image
 * @param {Object} imageObject a fabric object
 * @param {Object} borderConfig border seting
 */
export const applyBorderToImageObject = (imageObject, borderConfig) => {
  const imageProp = toFabricImageBorderProp(borderConfig);

  const strokeLineCap = getStrokeLineCap(borderConfig.strokeLineType);

  imageObject.set({
    ...imageProp,
    strokeLineCap,
    dirty: true
  });
};

export const checkLoadImage = imgSrc => {
  return new Promise(resolve => {
    fabric.util.loadImage(
      modifyUrl(imgSrc),
      img => {
        resolve(img);
      },
      null,
      {
        crossOrigin: 'anonymous'
      }
    );
  });
};

/**
 * Handle replace source url of Image box
 * @param {Element} imageObject object to replace source
 * @param {String} imageSrc Source image for replace
 */
export const setImageSrc = async (imageObject, imageSrc) => {
  const control = await createMediaOverlay(IMAGE_LOCAL.CONTROL_ICON, {
    width: CROP_CONTROL.WIDTH,
    height: CROP_CONTROL.HEIGHT
  });

  return new Promise(resolve => {
    const { width, height, scaleX, scaleY } = imageObject;
    const src = imageSrc || IMAGE_LOCAL.PLACE_HOLDER;
    const hasImage = !!imageSrc;
    imageObject.setSrc(
      modifyUrl(src),
      img => {
        const newScaleX = (width * scaleX) / img.width;
        const newScaleY = (height * scaleY) / img.height;

        const newProp = {
          imageUrl: src,
          hasImage,
          scaleX: newScaleX,
          scaleY: newScaleY,
          width: img.width,
          height: img.height,
          objectType: OBJECT_TYPE.IMAGE,
          control,
          showControl: false
        };

        img.set(newProp);

        applyShadowToObject(img, imageObject);

        if (hasImage) {
          centercrop(imageObject);
          newProp.zoomLevel = img.zoomLevel;
        }

        const props = {
          type: OBJECT_TYPE.IMAGE,
          hasImage,
          imageUrl: src,
          zoomLevel: newProp.zoomLevel
        };

        if (src === IMAGE_LOCAL.PLACE_HOLDER) props.imageId = null;

        resolve(props);
      },
      {
        crossOrigin: 'anonymous'
      }
    );
  });
};

/**
 * Handle replace source url of Image box
 * @param {Element} imageObject object to replace source
 * @param {String} imageSrc Source image for replace
 */
export const setNewImageSrc = async (imageObject, imageSrc, isPreviewPDF) => {
  const control = await createMediaOverlay(IMAGE_LOCAL.CONTROL_ICON, {
    width: CROP_CONTROL.WIDTH,
    height: CROP_CONTROL.HEIGHT
  });

  const { width, height, scaleX, scaleY, lowsizeUrl } = imageObject;
  const src = imageSrc || IMAGE_LOCAL.PLACE_HOLDER;
  const hasImage = !!imageSrc;
  const image = imageObject;
  const newScaleX = (width * scaleX) / image.width;
  const newScaleY = (height * scaleY) / image.height;

  const newProp = {
    imageUrl: src,
    lowsizeUrl: hasImage ? lowsizeUrl : null,
    hasImage,
    scaleX: newScaleX,
    scaleY: newScaleY,
    width: image.width,
    height: image.height,
    objectType: OBJECT_TYPE.IMAGE,
    control,
    showControl: false
  };

  image.set(newProp);

  applyShadowToObject(image, imageObject);
  const props = {
    type: OBJECT_TYPE.IMAGE,
    hasImage,
    imageUrl: src,
    lowsizeUrl: lowsizeUrl
  };

  if (newProp.zoomLevel) {
    props.zoomLevel = newProp.zoomLevel;
  }

  if (src === IMAGE_LOCAL.PLACE_HOLDER) {
    props.originalUrl = null;
    props.lowsizeUrl = null;
    props.imageId = null;
    imageObject.set({
      originalUrl: null,
      lowsizeUrl: null,
      cropInfo: null
    });
  }

  let imageUrl = props.imageUrl;
  if (!isPreviewPDF && hasImage) {
    imageUrl = await generateImageBlob(lowsizeUrl, imageUrl);
  }
  const img = await createMediaOverlay(imageUrl);
  imageObject.set({ img });
  return props;
};

/**
 * Handle to get list activate Image box
 * @returns {Array} List activate Image box
 */
export const getAvailableImages = () => {
  return getActiveCanvas()
    .getObjects()
    .filter(
      object => object.objectType === OBJECT_TYPE.IMAGE && !object.hasImage
    );
};

/**
 * Handle to get list activate Video box
 * @returns {Array} List activate Video box
 */
export const getAvailableVideos = () => {
  return getActiveCanvas()
    .getObjects()
    .filter(
      object => object.objectType === OBJECT_TYPE.VIDEO && !object.hasImage
    );
};

/**
 *
 * @param {Element} imageObject Source image for crop
 */
export const centercrop = imageObject => {
  const ele = imageObject._element || imageObject.img;
  if (!ele) return {};

  const { width: elWidth, height: elHeight } = ele;
  const { width, height, scaleX, scaleY } = imageObject;

  const xZoom = (width * scaleX - elWidth) / elWidth;
  const yZoom = (height * scaleY - elHeight) / elHeight;
  const zoomLevel = Math.max(xZoom, yZoom);

  imageObject.set({ zoomLevel });

  return { zoomLevel };
};

/**
 * Handle when dragging over image box
 * @param {Object} target - Image object has applied drag trigger
 */
export const handleDragEnter = ({ target }) => {
  if (target.cachedStrokeData || !target.selectable) return;

  const cachedStrokeData = {
    stroke: target.stroke,
    strokeWidth: target.strokeWidth
  };

  target.set({
    cachedStrokeData,
    stroke: IMAGE_INDICATOR.STROKE,
    strokeWidth: IMAGE_INDICATOR.STROKE_WIDTH
  });

  getActiveCanvas().renderAll();
};

/**
 * Handle when dragging leave image box
 * @param {*} target - Image object has applied drag trigger
 */
export const handleDragLeave = ({ target }) => {
  if (isEmpty(target.cachedStrokeData) || !target.selectable) return;

  const { stroke, strokeWidth } = target.cachedStrokeData;
  target.set({
    stroke,
    strokeWidth,
    cachedStrokeData: null
  });

  getActiveCanvas().renderAll();
};

/**
 * Handle hover on fabric object
 * @param {Object} event - Event when hover object
 */
export const handleMouseMove = event => {
  const target = event.target;

  if (target?.objectType === OBJECT_TYPE.IMAGE) {
    handleHoverImage(target);
  }

  if (target?.objectType === OBJECT_TYPE.VIDEO) {
    handleHoverVideo(target);
  }
};

/**
 * Handle when hover on control icon
 * @param {Object} target Fabric object selected
 */
const handleHoverImage = target => {
  if (!target.hasImage || !target.selectable) {
    target.set({
      hoverCursor: null,
      isHoverControl: false
    });
    return;
  }

  const { width, height, scaleX, scaleY, canvas } = target;

  const { x, y } = target.getLocalPointer();

  const zoom = canvas.getZoom();

  const eleWidth = width * scaleX;
  const eleHeight = height * scaleY;

  const iconDimension = eleWidth / 6;

  const controlEndX = eleWidth / 2;
  const controlStartX = controlEndX - iconDimension;

  const controlEndY = eleHeight - 15 / zoom;
  const controlStartY = controlEndY - iconDimension;

  const containPointerX = x >= controlStartX && x <= controlEndX;
  const containPointerY = y >= controlStartY && y <= controlEndY;

  const prop =
    containPointerX && containPointerY
      ? {
          hoverCursor: 'pointer',
          isHoverControl: true
        }
      : {
          hoverCursor: null,
          isHoverControl: false
        };

  target.set(prop);
};

/**
 * Handle when hover on play icon
 * @param {Object} target Fabric object selected
 */
const handleHoverVideo = target => {
  if (!target.showPlayIcon) {
    return target.set({
      hoverCursor: null,
      isHoverPlayIcon: false
    });
  }

  const { x, y } = target.getLocalPointer();
  const { width, height, scaleX, scaleY } = target;

  const startX = (width * scaleX - VIDEO_PLAY_ICON.WIDTH) / 2;
  const endX = startX + VIDEO_PLAY_ICON.WIDTH;

  const startY = (height * scaleY - VIDEO_PLAY_ICON.HEIGHT) / 2;
  const endY = startY + VIDEO_PLAY_ICON.HEIGHT;

  const containPointerX = x >= startX && x <= endX;
  const containPointerY = y >= startY && y <= endY;

  const prop =
    containPointerX && containPointerY
      ? {
          hoverCursor: 'pointer',
          isHoverPlayIcon: true
        }
      : {
          hoverCursor: null,
          isHoverPlayIcon: false
        };

  target.set(prop);
};

/**
 * Handle when hover element
 * @param {Object} target - fabric object
 * @param {Boolean} isAnimationOpen - whether animation property open or not, if it open, do not show mapping icon
 */
export const handleMouseOver = ({ target }, isAnimationOpen) => {
  isAnimationOpen || renderMappingIcon(target);

  // handle render crop control icon
  if (target?.objectType !== OBJECT_TYPE.IMAGE) return;

  renderImageCropControl(target);
};

/**
 * Handle when leave element
 * @param {Object} target - fabric object
 */
export const handleMouseOut = ({ target }) => {
  if (
    target?.objectType !== OBJECT_TYPE.IMAGE ||
    !target.hasImage ||
    !target.selectable
  ) {
    target?.canvas.renderAll();
    return;
  }

  target.set({
    showControl: false,
    dirty: true
  });

  target.canvas.renderAll();
};

/**
 * Create element for video object
 * @param {String} src video url
 * @return video element
 */
export const createVideoElement = src =>
  new Promise(resolve => {
    const ele = document.createElement('video');
    ele.setAttribute('preload', 'metadata');
    ele.setAttribute('controls', true);
    ele.setAttribute('playsinline', '');
    ele.crossOrigin = 'anonymous';

    ele.addEventListener(
      'loadedmetadata',
      () => {
        ele.width = ele.videoWidth;
        ele.height = ele.videoHeight;

        resolve(ele);
      },
      false
    );

    if (src || src !== IMAGE_LOCAL.PLACE_HOLDER)
      ele.src = `${getOriginalUrl(src)}?#t=0.01`;
  });

/**
 * Create element for video thumbnail
 * @param {String} src video's thumbnail url
 * @param {String} options video's thumbnail options
 * @return image element
 */
export const createMediaOverlay = (src, options) => {
  return new Promise((resolve, reject) => {
    const ele = document.createElement('img');
    if (options?.width) {
      ele.width = options.width;
    }

    if (options?.height) {
      ele.height = options.height;
    }

    ele.onload = () => resolve(ele);
    ele.onerror = () => reject('Cannot load image');

    ele.crossOrigin = 'anonymous';
    if (src) ele.src = modifyUrl(src);
  });
};

/**
 * Render video by video frames
 */
const reqAnimFrame = renderFn => {
  let activeCanvas = null;
  if (typeof getActiveCanvas !== 'undefined') {
    activeCanvas = getActiveCanvas();
  } else {
    activeCanvas = window.mainCanvas;
  }
  if (!activeCanvas) return;
  activeCanvas.renderAll();
  const objects = activeCanvas.getObjects();

  const isPlaying = objects.some(obj => obj.isPlaying);

  if (isPlaying) {
    fabric.util.requestAnimFrame(renderFn);
  }
};

/**
 * Render video by video frames
 */
export const requestAnimFrame = (isSeek = false, obj = null) => {
  fabric.util.requestAnimFrame(function render() {
    if (obj) obj.set({ dirty: true });

    if (!isSeek) {
      reqAnimFrame(render);

      return;
    }

    setTimeout(() => reqAnimFrame(render), 350);
  });
};

/**
 * Set video element for fabric image object
 * @param {Element} imageObject selected object to set video element
 * @param {String} videoSrc video url will be set to object
 * @param {String} thumbnailSrc video's thumbnail url will be set to object
 * @param {Function} videoToggleStatusCallback method call when video stop
 */
export const setVideoSrc = async (
  imageObject,
  videoSrc,
  thumbnailSrc,
  videoToggleStatusCallback
) => {
  const { width, height, scaleX, scaleY, customThumbnailUrl } = imageObject;
  if (imageObject.objectType === OBJECT_TYPE.VIDEO) {
    imageObject.dispose();

    imageObject.set({ volume: DEFAULT_VIDEO.VOLUME, isPlaying: false });
  }

  videoInitEvent(imageObject);

  // Handle here to load cache video for playback preview use cache
  let cacheVideo = null;
  if (window.listVideos) {
    cacheVideo = window.listVideos.find(
      item => Object.keys(item)[0] === imageObject.id
    )[imageObject.id];
  }
  const video = cacheVideo ? cacheVideo : await createVideoElement(videoSrc);

  video.currentTime = 0;

  const volume = imageObject.get('volume');

  video.volume = (isEmpty(volume) ? DEFAULT_VIDEO.VOLUME : volume) / 100;

  const unPlayProperties = {
    isPlaying: false,
    showPlayIcon: true,
    dirty: true
  };

  video.addEventListener(VIDEO_EVENT_TYPE.PLAY, () => {
    imageObject.set({
      isPlaying: true,
      showThumbnail: false,
      showPlayIcon: false,
      dirty: true
    });

    requestAnimFrame(false, imageObject);
  });

  video.addEventListener(VIDEO_EVENT_TYPE.PAUSE, () => {
    if (video.isKeepRewind) return;

    imageObject.set(unPlayProperties);

    requestAnimFrame();
  });

  video.addEventListener(VIDEO_EVENT_TYPE.ENDED, () => {
    if (video.isKeepRewind) return;

    imageObject.set({
      ...unPlayProperties,
      showThumbnail: true
    });

    video.isTempPlaying = false;

    video.currentTime = 0;

    requestAnimFrame();

    videoToggleStatusCallback(imageObject.id);
  });

  video.addEventListener(VIDEO_EVENT_TYPE.SEEK, () => {
    if (!imageObject.isPlaying) {
      imageObject.set({
        showThumbnail: false,
        showPlayIcon: true,
        dirty: true
      });
    }

    requestAnimFrame(true);
  });

  video.addEventListener(VIDEO_EVENT_TYPE.REWIND, () => {
    imageObject.set({
      showThumbnail: false,
      showPlayIcon: false,
      dirty: true
    });

    requestAnimFrame();
  });

  video.addEventListener(VIDEO_EVENT_TYPE.END_REWIND, () => {
    imageObject.set(unPlayProperties);

    requestAnimFrame();

    videoToggleStatusCallback(imageObject.id);
  });

  video.addEventListener(VIDEO_EVENT_TYPE.TOGGLE_STATUS, () => {
    videoToggleStatusCallback(imageObject.id, imageObject.get('isPlaying'));
  });

  imageObject.setElement(video);

  const getThumbnail = createMediaOverlay(customThumbnailUrl || thumbnailSrc);

  const getPlayIcon = createMediaOverlay(IMAGE_LOCAL.PLAY_ICON, {
    width: VIDEO_PLAY_ICON.WIDTH,
    height: VIDEO_PLAY_ICON.HEIGHT
  });

  const [thumbnail, playIcon] = await Promise.all([getThumbnail, getPlayIcon]);

  const newScaleX = (width * scaleX) / video.width;

  const newScaleY = (height * scaleY) / video.height;

  const newProp = {
    scaleX: newScaleX,
    scaleY: newScaleY,
    width: video.width,
    height: video.height,
    hasImage: true,
    thumbnail,
    playIcon,
    showThumbnail: true,
    showPlayIcon: true,
    thumbnailUrl: thumbnailSrc,
    objectType: OBJECT_TYPE.VIDEO
  };

  imageObject.set(newProp);

  applyShadowToObject(imageObject, imageObject);

  const { zoomLevel } = centercrop(imageObject);
  const duration = Math.ceil(video.duration);

  return {
    type: OBJECT_TYPE.VIDEO,
    imageUrl: videoSrc,
    thumbnailUrl: thumbnailSrc,
    zoomLevel,
    duration,
    endTime: duration
  };
};

/**
 * Handle change media src in image box
 * @param {Element} target current image box will apply new src
 * @param {Object} options new prop for image box
 * @param {Function} videoToggleStatusCallback method call when video stop
 * @returns new properties of image box after change src
 */
export const handleChangeMediaSrc = async (
  target,
  options,
  videoToggleStatusCallback = null
) => {
  if (!target) return;

  const { imageUrl, id, mediaUrl, thumbUrl } = options;

  const prop = mediaUrl
    ? await setVideoSrc(target, mediaUrl, thumbUrl, videoToggleStatusCallback)
    : await setNewImageSrc(target, imageUrl);

  if (!mediaUrl) {
    const { zoomLevel } = centercrop(target);
    target.set({
      zoomLevel
    });
  }

  prop.imageId = id;
  prop.originalUrl = imageUrl;

  target.set({
    originalUrl: imageUrl,
    cropInfo: null,
    fromPortrait: false
  });

  if (mediaUrl) {
    prop.volume = DEFAULT_VIDEO.VOLUME;
    prop.endTime = prop.duration;
    prop.startTime = 0;
  }

  target.set({
    dirty: true
  });

  target.canvas.renderAll();
  return { id: target.id, prop };
};

/**
 * Create new fabric object width initial properties
 * @param {Object} props initial properties of portrait image
 * @returns {Promise<Object>} instance of fabric object
 */
export const createPortraitImage = async props => {
  const {
    top,
    left,
    width,
    height,
    mask,
    imageUrl,
    objectType,
    id,
    fromPortrait
  } = toFabricPortraitImageProp(props);

  const radiusRatio = mask === PORTRAIT_IMAGE_MASK.ROUNDED ? 10 : 2;

  const rect = new fabric.Rect({
    id,
    top,
    left,
    width,
    height,
    rx: width / radiusRatio,
    ry: width / radiusRatio,
    mask,
    objectType,
    fill: 'transparent',
    strokeUniform: true,
    fromPortrait,
    imageUrl
  });

  try {
    const img = await createMediaOverlay(imageUrl);

    rect.set({ img });
    return rect;
  } catch (error) {
    throw new Error(error);
  }
};

export const createMediaObject = async (
  mediaProperties,
  videoToggleCallback,
  isPreviewPDF
) => {
  const isVideo = mediaProperties?.type === OBJECT_TYPE.VIDEO;
  let mediaObject;
  if (isVideo) {
    mediaObject = await createImage(mediaProperties);
    videoInitEvent(mediaObject.object);
  } else {
    mediaObject = await createNewImage(mediaProperties, isPreviewPDF);
  }

  const media = mediaObject?.object;

  useObjectControlsOverride(media);

  const {
    border,
    hasImage,
    control,
    type,
    imageUrl,
    thumbnailUrl,
    customThumbnailUrl
  } = mediaProperties;
  const url = customThumbnailUrl || thumbnailUrl;
  if (type === OBJECT_TYPE.VIDEO && url) {
    await setVideoSrc(media, imageUrl, url, videoToggleCallback);
  }
  imageBorderModifier(media);

  if (!isVideo) useDoubleStroke(media);

  const {
    dropShadow,
    shadowBlur,
    shadowOffset,
    shadowOpacity,
    shadowAngle,
    shadowColor
  } = media;

  applyShadowToObject(media, {
    dropShadow,
    shadowBlur,
    shadowOffset,
    shadowOpacity,
    shadowAngle,
    shadowColor
  });

  applyBorderToImageObject(media, border);

  updateSpecificProp(media, {
    coord: {
      rotation: mediaProperties.coord.rotation
    },
    cropInfo: mediaProperties.cropInfo
  });

  if (type === OBJECT_TYPE.IMAGE && hasImage && !control) {
    await setNewImageSrc(media, media.imageUrl, isPreviewPDF);
    centercrop(media);
  }

  return media;
};

export const createPortraitImageObject = async properties => {
  const image = await createPortraitImage(properties);

  const { border, shadow } = properties;

  useDoubleStroke(image);

  useObjectControlsOverride(image);

  applyShadowToObject(image, shadow);

  applyBorderToImageObject(image, border);

  return image;
};

export const applyCornerRoundingForImage = (element, properties) => {
  const { cornerRounding } = properties;
  let currentCornerRounding = 0;
  if (isEmpty(cornerRounding)) {
    if (element.cornerRounding) {
      const minValue = Math.min(element.width, element.height);
      const minValuPt = pxToPt(minValue);
      currentCornerRounding = Math.min(element.cornerRounding, minValuPt / 2);
    }
  } else {
    currentCornerRounding = cornerRounding;
  }

  const { rx, ry } = calculateRxRy({
    ...element,
    cornerRounding: currentCornerRounding
  });

  element.set({
    cornerRounding: currentCornerRounding,
    dirty: true,
    rx,
    ry
  });
  element.canvas.renderAll();
  return {
    cornerRounding: currentCornerRounding
  };
};
